// ---------------------------------------------------------------------------
// Mount the page again when the page changes (R12c, rule 5)
//
// B6: edit X → "New campaign" rendered the same CampaignWizard function into
// the same part, and functional-lit keys a component by its function per part
// (stateful.js:116-135). So the wizard kept X's draft and _id, its leave hook
// and its autosave timers, and later saves went to X.
//
// Lit's `keyed` cannot fix that for functional-lit pages
// (scratchpad/r12/review-probe/keyed-stateful.mjs): keyed(key, page) keeps
// the state, and keyed(key, html`${page}`) starts fresh but never runs the
// old page's effect cleanups — keyed resets the part's committed value before
// clearing it, so Lit never disconnects the old instance, and functional-lit
// runs cleanups only on disconnect (stateful.js:132-136, 147-154). Leaving
// the wizard would then keep its leave hook, and every later navigation
// would run a dead wizard's save.
//
// remount(key, content) clears the part itself when the key changes — Lit's
// clearPart, which disconnects what was there, so the old page's cleanups
// run — and then commits the content as new. The content goes into a
// template of its own, so it gets a part of its own.
//
// The directive must sit INSIDE the guard (app-root.ts), never around it:
// clearing the guard's part would disconnect Adornis's guard, which
// unsubscribes from the user feed and does not subscribe again
// (authentication-guard.js:18-40).
//
// After it has committed a new key, it reports the key and what it rendered
// to onRemount's listeners (page-change.ts), once the render has finished:
// the focus step looks for the new page's h1 there, and only there.
// ---------------------------------------------------------------------------

import { html } from 'lit';
import { type ChildPart, Directive, directive, type PartInfo, PartType } from 'lit/directive.js';
import { clearPart, setCommittedValue } from 'lit/directive-helpers.js';

/** What a remount rendered for its key. */
export interface RemountScope {
  /** The page's top-level elements, as they are now. */
  elements(): Element[];
  /** True when the node is inside what was rendered for the key. */
  contains(node: Node | null): boolean;
  /** The first match inside what was rendered for the key. */
  query<E extends Element = Element>(selector: string): E | null;
  /** The node the content sits in, for a MutationObserver. */
  readonly container: Node | null;
}

type Listener = (key: string, scope: RemountScope) => void;

const listeners = new Set<Listener>();

/** Called after each remount has committed a new key (and after the first mount). */
export function onRemount(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function scopeOf(part: ChildPart): RemountScope {
  const elements = () => {
    const out: Element[] = [];
    const end = part.endNode;
    for (let node = part.startNode?.nextSibling ?? null; node && node !== end; node = node.nextSibling) {
      if (node.nodeType === 1) out.push(node as Element);
    }
    return out;
  };
  return {
    elements,
    contains: node => !!node && elements().some(el => el === node || el.contains(node)),
    query: <E extends Element = Element>(selector: string) => {
      for (const el of elements()) {
        if (el.matches(selector)) return el as E;
        const found = el.querySelector<E>(selector);
        if (found) return found;
      }
      return null;
    },
    get container() {
      return part.parentNode;
    },
  };
}

class Remount extends Directive {
  private key: string | undefined;

  constructor(partInfo: PartInfo) {
    super(partInfo);
    if (partInfo.type !== PartType.CHILD) throw new Error('remount() belongs in a child part');
  }

  override render(_key: string, content: unknown) {
    return html`${content}`;
  }

  override update(part: ChildPart, [key, content]: [string, unknown]) {
    const changed = key !== this.key;
    if (changed && this.key !== undefined) {
      clearPart(part);
      setCommittedValue(part);
    }
    this.key = key;
    if (changed) {
      // The render this update belongs to finishes synchronously; a microtask runs after it.
      queueMicrotask(() => {
        if (this.key !== key || !part.parentNode) return;
        const scope = scopeOf(part);
        for (const listener of [...listeners]) {
          try {
            listener(key, scope);
          } catch (error) {
            console.error('[router] a remount listener failed', error);
          }
        }
      });
    }
    return this.render(key, content);
  }
}

/** Renders `content`, and renders it as new — the old one disconnected — whenever `key` changes. */
export const remount = directive(Remount);
